import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  X,
  Terminal,
  Code,
  ChevronRight,
  ChevronDown,
  Save,
  Search,
  Loader2,
  Check,
} from 'lucide-react'
import { useGeekMode } from '../contexts/GeekModeContext'
import { getPrompts, getPromptDefaults, updatePrompt, PromptTemplate } from '../services/api'

export default function GeekModePanel() {
  const queryClient = useQueryClient()
  const { isEnabled, isPanelOpen, togglePanel } = useGeekMode()
  const [selectedPromptName, setSelectedPromptName] = useState<string | null>(null)
  const [editedContent, setEditedContent] = useState('')
  const [editedTemperature, setEditedTemperature] = useState<number>(0.6)
  const [editedMaxTokens, setEditedMaxTokens] = useState<number>(16000)
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(['artifact_gen'])
  )
  const [showSaveSuccess, setShowSaveSuccess] = useState(false)

  // Fetch all prompts from database
  const { data: dbPrompts = [], isLoading: loadingDbPrompts } = useQuery({
    queryKey: ['geekmode-prompts'],
    queryFn: async () => {
      const response = await getPrompts()
      return response.data as PromptTemplate[]
    },
    enabled: isEnabled && isPanelOpen,
  })

  // Fetch default prompts (hardcoded)
  const { data: defaultPrompts = [], isLoading: loadingDefaults } = useQuery({
    queryKey: ['geekmode-defaults'],
    queryFn: async () => {
      const response = await getPromptDefaults()
      return response.data
    },
    enabled: isEnabled && isPanelOpen,
  })

  // Merge default prompts with DB overrides
  const allPrompts = useMemo(() => {
    const promptMap = new Map<string, PromptTemplate & { isOverridden?: boolean }>()

    // Add defaults first
    defaultPrompts.forEach(
      (def: { name: string; display_name: string; output_type: string; prompt: string }) => {
        promptMap.set(def.name, {
          id: def.name,
          name: def.name,
          display_name: def.display_name,
          description: `Generate ${def.output_type} artifacts`,
          category: 'artifact_gen',
          system_prompt: '',
          user_prompt_template: def.prompt,
          temperature: 0.6,
          max_tokens: 16000,
          is_active: true,
          version: 1,
          created_at: '',
          updated_at: '',
          isOverridden: false,
        })
      }
    )

    // Override with DB prompts
    dbPrompts.forEach((dbPrompt) => {
      if (promptMap.has(dbPrompt.name)) {
        promptMap.set(dbPrompt.name, { ...dbPrompt, isOverridden: true })
      } else {
        promptMap.set(dbPrompt.name, { ...dbPrompt, isOverridden: true })
      }
    })

    return Array.from(promptMap.values())
  }, [dbPrompts, defaultPrompts])

  // Group prompts by category
  const groupedPrompts = useMemo(() => {
    const groups: Record<string, (PromptTemplate & { isOverridden?: boolean })[]> = {}
    const filtered = allPrompts.filter(
      (p) =>
        searchQuery === '' ||
        p.display_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
    filtered.forEach((prompt) => {
      const category = prompt.category || 'other'
      if (!groups[category]) groups[category] = []
      groups[category].push(prompt)
    })
    return groups
  }, [allPrompts, searchQuery])

  const sortedCategories = useMemo(() => {
    return Object.keys(groupedPrompts).sort()
  }, [groupedPrompts])

  // Selected prompt details
  const selectedPrompt = useMemo(() => {
    return allPrompts.find((p) => p.name === selectedPromptName)
  }, [allPrompts, selectedPromptName])

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({
      name,
      updates,
    }: {
      name: string
      updates: { user_prompt_template?: string; temperature?: number; max_tokens?: number }
    }) => {
      const response = await updatePrompt(name, updates)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['geekmode-prompts'] })
      setShowSaveSuccess(true)
      setTimeout(() => setShowSaveSuccess(false), 2000)
    },
  })

  // Handle prompt selection
  const handleSelectPrompt = (prompt: PromptTemplate) => {
    setSelectedPromptName(prompt.name)
    setEditedContent(prompt.user_prompt_template)
    setEditedTemperature(prompt.temperature)
    setEditedMaxTokens(prompt.max_tokens)
  }

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev)
      if (next.has(category)) {
        next.delete(category)
      } else {
        next.add(category)
      }
      return next
    })
  }

  const hasChanges =
    selectedPrompt &&
    (editedContent !== selectedPrompt.user_prompt_template ||
      editedTemperature !== selectedPrompt.temperature ||
      editedMaxTokens !== selectedPrompt.max_tokens)

  const handleSave = () => {
    if (!selectedPromptName || !hasChanges) return

    updateMutation.mutate({
      name: selectedPromptName,
      updates: {
        user_prompt_template: editedContent,
        temperature: editedTemperature,
        max_tokens: editedMaxTokens,
      },
    })
  }

  if (!isEnabled || !isPanelOpen) {
    return null
  }

  const isLoading = loadingDbPrompts || loadingDefaults

  return (
    <div
      className="fixed right-0 top-0 h-full z-40 flex"
      style={{
        width: '700px',
        transition: 'width 0.3s ease',
      }}
    >
      {/* Main Panel */}
      <div
        className="w-full h-full flex flex-col"
        style={{
          background: 'rgba(0, 10, 0, 0.98)',
          borderLeft: '1px solid rgba(0, 255, 0, 0.3)',
          boxShadow: '-4px 0 20px rgba(0, 255, 0, 0.1)',
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-4 py-3"
          style={{ borderBottom: '1px solid rgba(0, 255, 0, 0.2)' }}
        >
          <div className="flex items-center space-x-2">
            <div
              className="font-mono font-bold text-sm tracking-wider flex items-center"
              style={{
                color: '#00ff00',
                textShadow: '0 0 10px rgba(0, 255, 0, 0.5)',
              }}
            >
              <Terminal className="w-4 h-4 mr-2" />
              GEEK MODE - Prompt Editor
            </div>
          </div>
          <button
            onClick={togglePanel}
            className="p-1 hover:opacity-70 transition-opacity"
            style={{ color: '#00ff00' }}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Sidebar - Prompt List */}
          <div
            className="w-[200px] h-full flex flex-col"
            style={{ borderRight: '1px solid rgba(0, 255, 0, 0.2)' }}
          >
            {/* Search */}
            <div className="p-2" style={{ borderBottom: '1px solid rgba(0, 255, 0, 0.1)' }}>
              <div className="relative">
                <Search
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3"
                  style={{ color: 'rgba(0, 255, 0, 0.4)' }}
                />
                <input
                  type="text"
                  placeholder="Search prompts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-7 pr-2 py-1 font-mono text-xs rounded"
                  style={{
                    background: 'rgba(0, 255, 0, 0.05)',
                    border: '1px solid rgba(0, 255, 0, 0.2)',
                    color: '#00ff00',
                    outline: 'none',
                  }}
                />
              </div>
            </div>

            {/* Categories & Prompts */}
            <div className="flex-1 overflow-y-auto">
              {isLoading ? (
                <div
                  className="flex items-center justify-center h-20 font-mono text-xs"
                  style={{ color: 'rgba(0, 255, 0, 0.5)' }}
                >
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Loading...
                </div>
              ) : (
                sortedCategories.map((category) => (
                  <div key={category}>
                    <button
                      onClick={() => toggleCategory(category)}
                      className="w-full flex items-center justify-between px-2 py-1.5 font-mono text-[10px] uppercase tracking-wider hover:bg-opacity-100"
                      style={{
                        background: 'rgba(0, 255, 0, 0.05)',
                        color: 'rgba(0, 255, 0, 0.7)',
                        borderBottom: '1px solid rgba(0, 255, 0, 0.1)',
                      }}
                    >
                      <span>{category.replace('_', ' ')}</span>
                      <span className="flex items-center">
                        <span className="mr-2 text-[9px]" style={{ color: 'rgba(0, 255, 0, 0.4)' }}>
                          {groupedPrompts[category].length}
                        </span>
                        {expandedCategories.has(category) ? (
                          <ChevronDown className="w-3 h-3" />
                        ) : (
                          <ChevronRight className="w-3 h-3" />
                        )}
                      </span>
                    </button>
                    {expandedCategories.has(category) && (
                      <div>
                        {groupedPrompts[category].map((prompt) => (
                          <button
                            key={prompt.name}
                            onClick={() => handleSelectPrompt(prompt)}
                            className="w-full text-left px-3 py-1.5 font-mono text-[10px] transition-all"
                            style={{
                              background:
                                selectedPromptName === prompt.name
                                  ? 'rgba(0, 255, 0, 0.15)'
                                  : 'transparent',
                              color:
                                selectedPromptName === prompt.name
                                  ? '#00ff00'
                                  : 'rgba(0, 255, 0, 0.6)',
                              borderLeft:
                                selectedPromptName === prompt.name
                                  ? '2px solid #00ff00'
                                  : '2px solid transparent',
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <span className="truncate">{prompt.display_name}</span>
                              {prompt.isOverridden && (
                                <span
                                  className="text-[8px] px-1 rounded"
                                  style={{
                                    background: 'rgba(0, 255, 255, 0.2)',
                                    color: '#00ffff',
                                  }}
                                >
                                  DB
                                </span>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Main Editor Area */}
          <div className="flex-1 flex flex-col">
            {!selectedPromptName ? (
              <div
                className="flex-1 flex flex-col items-center justify-center font-mono"
                style={{ color: 'rgba(0, 255, 0, 0.4)' }}
              >
                <Code className="w-12 h-12 mb-4" />
                <div className="text-sm">Select a prompt to edit</div>
                <div className="text-[10px] mt-2">
                  Expand a category on the left to browse prompts
                </div>
              </div>
            ) : selectedPrompt ? (
              <>
                {/* Header */}
                <div
                  className="px-4 py-3"
                  style={{ borderBottom: '1px solid rgba(0, 255, 0, 0.2)' }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="font-mono text-sm font-bold" style={{ color: '#00ff00' }}>
                        {selectedPrompt.display_name}
                      </div>
                      <div
                        className="font-mono text-[10px]"
                        style={{ color: 'rgba(0, 255, 0, 0.5)' }}
                      >
                        {selectedPrompt.name}
                      </div>
                    </div>
                    {selectedPrompt.isOverridden && (
                      <span
                        className="px-2 py-1 rounded font-mono text-[10px]"
                        style={{
                          background: 'rgba(0, 255, 255, 0.2)',
                          color: '#00ffff',
                        }}
                      >
                        Customized in DB
                      </span>
                    )}
                  </div>

                  {/* Settings Row */}
                  <div className="flex items-center space-x-4 mt-3">
                    <div className="flex items-center space-x-2">
                      <label
                        className="font-mono text-[10px]"
                        style={{ color: 'rgba(0, 255, 0, 0.6)' }}
                      >
                        Temperature:
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="2"
                        step="0.1"
                        value={editedTemperature}
                        onChange={(e) => setEditedTemperature(parseFloat(e.target.value) || 0)}
                        className="w-16 px-2 py-1 font-mono text-[10px] rounded"
                        style={{
                          background: 'rgba(0, 255, 0, 0.1)',
                          border: '1px solid rgba(0, 255, 0, 0.3)',
                          color: '#00ff00',
                          outline: 'none',
                        }}
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <label
                        className="font-mono text-[10px]"
                        style={{ color: 'rgba(0, 255, 0, 0.6)' }}
                      >
                        Max Tokens:
                      </label>
                      <input
                        type="number"
                        min="100"
                        max="128000"
                        step="1000"
                        value={editedMaxTokens}
                        onChange={(e) => setEditedMaxTokens(parseInt(e.target.value) || 4000)}
                        className="w-24 px-2 py-1 font-mono text-[10px] rounded"
                        style={{
                          background: 'rgba(0, 255, 0, 0.1)',
                          border: '1px solid rgba(0, 255, 0, 0.3)',
                          color: '#00ff00',
                          outline: 'none',
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Editor */}
                <div className="flex-1 p-4 overflow-hidden">
                  <textarea
                    value={editedContent}
                    onChange={(e) => setEditedContent(e.target.value)}
                    className="w-full h-full p-3 font-mono text-xs rounded resize-none"
                    style={{
                      background: 'rgba(0, 255, 0, 0.03)',
                      border: hasChanges
                        ? '2px solid rgba(255, 255, 0, 0.5)'
                        : '1px solid rgba(0, 255, 0, 0.2)',
                      color: 'rgba(0, 255, 0, 0.9)',
                      outline: 'none',
                    }}
                    placeholder="Enter prompt content..."
                  />
                </div>

                {/* Footer with save button */}
                <div
                  className="px-4 py-3 flex items-center justify-between"
                  style={{ borderTop: '1px solid rgba(0, 255, 0, 0.2)' }}
                >
                  <div
                    className="font-mono text-[10px]"
                    style={{ color: hasChanges ? '#ffff00' : 'rgba(0, 255, 0, 0.4)' }}
                  >
                    {showSaveSuccess ? (
                      <span className="flex items-center" style={{ color: '#00ff00' }}>
                        <Check className="w-3 h-3 mr-1" />
                        Saved!
                      </span>
                    ) : hasChanges ? (
                      'Unsaved changes'
                    ) : (
                      'No changes'
                    )}
                  </div>
                  <button
                    onClick={handleSave}
                    disabled={!hasChanges || updateMutation.isPending}
                    className="px-4 py-1.5 font-mono text-xs rounded transition-all disabled:opacity-50 flex items-center"
                    style={{
                      background: hasChanges ? 'rgba(0, 255, 0, 0.2)' : 'rgba(0, 255, 0, 0.05)',
                      border: `1px solid ${hasChanges ? 'rgba(0, 255, 0, 0.5)' : 'rgba(0, 255, 0, 0.2)'}`,
                      color: hasChanges ? '#00ff00' : 'rgba(0, 255, 0, 0.4)',
                    }}
                  >
                    {updateMutation.isPending ? (
                      <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-3 h-3 mr-2" />
                    )}
                    Save to Database
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>

        {/* Footer */}
        <div
          className="px-4 py-2 font-mono text-[9px] text-center"
          style={{
            borderTop: '1px solid rgba(0, 255, 0, 0.1)',
            color: 'rgba(0, 255, 0, 0.3)',
          }}
        >
          Edit prompts to tune AI artifact generation | Changes saved to database override defaults
        </div>
      </div>
    </div>
  )
}
